<template>
  <div>
    <div class="el-timeline-item__timestamp is-top">
      <span class="date">{{ formatDate(event.time) }}</span>
      <span>{{ formatTime(event.time) }}</span>
    </div>
    <h4>
      {{ event.type }}<small v-if="event.isEdited">({{$t('INDENT_EDITED')}})</small>
      <span
        class="f-right action-icon"
        v-on:click="openEditableView"
        v-if="isEditable"
      >
        <svgicon name="icon-edit-md" width="14" height="14" />
      </span>
    </h4>
    <p v-if="event.name"><span v-if="event.previous_vehicle_name">{{ event.previous_vehicle_name }} <i style="color: black;">→</i> </span> {{ event.name }} </p>
    <p v-if="event.new_time"><span v-if="event.previous_time">{{ formatDate(event.previous_time) }} {{formatTime(event.previous_time )}} <i style="color: black;">→</i>  </span> {{ formatDate(event.new_time) }} {{formatTime(event.new_time )}} </p>
    <p v-if="event.updated_by">{{ event.updated_by }}</p>
    <p v-if="event.reason && event.reason !== ''">{{$t('INDENT_REASON')}}: {{ event.reason }}
    <p v-if="event.pincode">{{ event.pincode }}</p>
    <p v-if="event.remarks">{{ event.remarks }}</p>
    <p v-if="event.statusCode">{{$t('INDENT_STATUS_CODE')}}: {{ event.statusCode }}</p>

    <p v-if="event.statusCode">
      <Button type="primary" size="xsmall" @click="showResponse = !showResponse">
        {{ showResponse ? $t('INDENT_HIDE') : $t('INDENT_SHOW') }} {{$t('INDENT_RESPONSE')}}
      </Button>
       <Card v-if="showResponse">
          <h4 class="card-list-title">{{ event.name }}</h4>
          <div class="popover-primary-body alignment scroll_section">
            {{ event.response }}
          </div>
        </Card>
    </p>

    <!--  start of edit box -->
    <Processing
      v-if="isLoadingConfirmView || isSaveLoading"
      background="#0073FF"
      size="30px"
    />
    <div class="card-list mt-10 mb-10" v-else-if="isEditableView">
      <h5 class="is-select">
        <label>{{$t('INDENT_EDIT_DATE_TIME')}}</label>
        <span>
          <Date-Picker
            :clearable="false"
            class="date-plain"
            :value="eventTime"
            format="dd-MM-yyyy hh:mm A"
            type="datetime"
            :picker-options="pickerOptions"
            :placeholder="$t('INDENT_SELECT_DATE_AND_TIME')"
            @input="changeTime($event)"
          >
          </Date-Picker>
          <span class="action-edit">
            <i class="text-grey-medium" @click="closeEditableView">
              <svgicon name="icon-close" width="8" height="8" />
            </i>
            <i class="text-blue" @click="checkDate">
              <svgicon name="icon-tick-sm" width="10" height="10" />
            </i>
          </span>
        </span>
      </h5>
    </div>
    <!--  end of edit box -->

    <!-- start of confirmation view -->
    <div class="timeline-msg" v-else-if="isConfirmView">
      <template v-if="confirmationTextArray && confirmationTextArray.length">
        <div class="text-xs mb-5">{{$t('INDENT_CHANGING_TIMESTAMP')}}:</div>
        <div
          class="timeline-badge mb-5 text-bold"
          v-for="(confirmationText, index) in confirmationTextArray"
          :key="index"
        >
          &#8226; {{ confirmationText.title }} :
          <span class="text-grey-dark text-lowercase">
            {{ confirmationText.formattedDiff }}
          </span>
        </div>
      </template>
      <template v-else>
        <div class="text-xs mb-5">
          {{$t('INDENT_CHAING_TIMESTAMP_PROMPT')}}
        </div>
      </template>
      <div class="text-right mt-10">
        <Button
          type="text"
          size="xsmall"
          class="text-primary"
          @click="closeConfirmView"
          >{{$t('INDENT_CANCEL')}}</Button
        >
        <Button type="primary" size="xsmall" @click="confirmDate"
          > {{$t('INDENT_YES_PROCEED')}}</Button
        >
      </div>
    </div>
    <!-- end of confirmation view -->
    <div v-if="shouldShowElapsedTime">
      <template
        v-for="(elapsedTimeConstant, index) in currentIndentElapsedTimeArray"
      >
        <div class="timeline-badge" :key="index">
          {{ elapsedTimeConstant.title }}
          <span class="timeline-badge-tag danger">
            {{ elapsedTimeConstant.formattedDiff }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
/* eslint-disable import/no-extraneous-dependencies */
import { deliveryTypes, orderType } from 'pando-constants';
import { isNumber, isDate } from 'lodash';
import moment from 'moment';
import editableTimeLines from '@/constants/orderTimeline';

export default {
  props: ['event', 'order', 'timeFormat'],
  data() {
    return {
      editableTimeLines,
      showResponse: false,
      isEditableView: false,
      isConfirmView: false,

      isLoadingConfirmView: false,
      isSaveLoading: false,

      eventTime: null,

      deliveryTypes,
      confirmationTextArray: [],
      pickerOptions: {
        disabledDate: (time) => {
          if (this.event.id === 'VEHICLE_ARRIVED') {
            const createdAt = new Date(this.$moment(this.event.created_at));
            createdAt.setHours(0, 0, 0, 0);
            return time.getTime() < createdAt.getTime();
          }
          return time.getTime() > Date.now();
        },
      },
    };
  },
  created() {
    this.eventTime = this.event.time;
  },
  computed: {
    orderId() {
      return this.order && this.order.order_number;
    },
    isEditable() {
      return (
        ![7, 8].includes(this.order.status) && // not in cancelled or delivered
        this.$hasPermission('edit_timestamps') &&
        Boolean(this.editableTimeLines.some(val => val.id === this.event.id))
      );
    },
    shouldShowElapsedTime() {
      return ![7, 8].includes(this.order.status);
    },
    timelineConstant() {
      return this.editableTimeLines.find(val => val.id === this.event.id);
    },
    elapsedTimeConstants() {
      const timelineConstant = this.timelineConstant;
      return (timelineConstant && timelineConstant.elapsed_times) || [];
    },
    currentIndentElapsedTimeArray() {
      if (!(this.isConfirmView || this.isEditableView)) {
        return this.indentElapsedTimeArray(this.order);
      }
      return [];
    },
  },
  methods: {
    indentElapsedTimeArray(orderDetails) {
      const elapsedTimeConstants = this.elapsedTimeConstants;
      const result = [];
      elapsedTimeConstants.forEach((elapsedConstant) => {
        const resultObj = {
          title: elapsedConstant.title,
        };
        let dateValue = null;
        if (elapsedConstant.id === 'loading_detention') {
          dateValue = this.getLoadingDetentionValue(orderDetails);
        } else if (elapsedConstant.id === 'acceptance_sla_breach') {
          dateValue = this.getAcceptanceSlaBreachValue(orderDetails);
        } else if (elapsedConstant.id === 'sla_breach') {
          dateValue = this.getSlaBreachValue(orderDetails);
        } else if (
          elapsedConstant.id === 'expected_delivery_date' &&
          orderDetails.order_type === orderType.CONTRACT
        ) {
          dateValue = this.getExpectedDeliveryDate(orderDetails);
        } else if (elapsedConstant.id === 'transit_delay') {
          dateValue = this.getTransitDelayValue(orderDetails);
        } else if (elapsedConstant.id === 'unloading_detention') {
          dateValue = this.getUnLoadingDetentionValue(orderDetails);
        }
        if (
          dateValue &&
          ['acceptance_sla_breach', 'sla_breach'].includes(elapsedConstant.id)
        ) {
          resultObj.formattedDiff = dateValue.value;
          result.push(resultObj);
        } else if (
          dateValue &&
          elapsedConstant.id === 'expected_delivery_date'
        ) {
          resultObj.formattedDiff =
            moment(dateValue).format('DD-MM-YYYY hh:mm A');
          result.push(resultObj);
        } else if (dateValue) {
          const formattedElapsedData = this.formatElapsedTime(dateValue);
          resultObj.formattedDiff = formattedElapsedData.value;
          result.push(resultObj);
        }
      });
      return result;
    },
    getUnLoadingDetentionValue(orderDetails) {
      if (this.isPtlOrder(orderDetails)) {
        return orderDetails.unloading_detention_duration;
      }
      const uniqueId = this.event.unique_id;
      const destination =
        orderDetails.destinations.find(
          _destination => _destination.unique_id === uniqueId,
        ) || {};
      return destination.unloading_detention_duration;
    },
    getExpectedDeliveryDate(orderDetails) {
      let expectedDeliveryDate = null;
      if (orderDetails.status !== 7) {
        orderDetails.destinations.forEach((destination) => {
          if (
            !expectedDeliveryDate ||
            destination.expected_delivery > expectedDeliveryDate
          ) {
            expectedDeliveryDate = destination.expected_delivery;
          }
        });
      }
      return expectedDeliveryDate;
    },
    getLoadingDetentionValue(orderDetails) {
      return orderDetails.loading_detention_duration;
    },
    getAcceptanceSlaBreachValue(orderDetails) {
      if (orderDetails.acceptance_breached_at) {
        const diff = this.formatElapsedTime(
          orderDetails.created_at,
          orderDetails.acceptance_breached_at,
        );
        return diff;
      }
      return null;
    },
    getSlaBreachValue(orderDetails) {
      if (orderDetails.arrival_breached_at) {
        const vehicleReqDate = moment.max(
          moment(orderDetails.created_at),
          moment(orderDetails.sources[0].loading_date),
        );
        const diff = this.formatElapsedTime(
          vehicleReqDate,
          orderDetails.arrival_breached_at,
        );
        return diff;
      }
      return null;
    },
    getTransitDelayValue(orderDetails) {
      if (!this.isPtlOrder(orderDetails)) {
        return orderDetails.transit_delay_duration;
      }

      const uniqueId = this.event.unique_id;
      const destination = orderDetails.destinations.find(
        _destination => _destination.unique_id === uniqueId,
      );
      if (!destination || !destination.transit_delay_duration) {
        let transitDelay = null;
        orderDetails.destinations.forEach((_destination) => {
          if (
            !transitDelay ||
            _destination.transit_delay_duration > transitDelay
          ) {
            transitDelay = _destination.expected_delivery;
          }
        });
        return transitDelay;
      }
      return destination.transit_delay_duration;
    },
    isPtlOrder(orderDetails) {
      return (
        orderDetails.vehicle_type &&
        orderDetails.vehicle_type.type === this.deliveryTypes.PTL
      );
    },
    /**
     * start of api calls
     */
    async checkDate() {
      try {
        this.isLoadingConfirmView = true;
        const data = [
          {
            unique_id: this.event.unique_id,
          },
        ];
        data[0][this.timelineConstant.api_key_date] = moment(
          this.eventTime,
        ).toISOString();
        const result = await this.$store.dispatch('order/checkDate', {
          orderId: this.orderId,
          url: this.timelineConstant.check_api,
          data,
        });
        if (!result) {
          this.$errorMessage({ message: this.$t('INDENT_DATE_SAVE_FAILED') });
        } else if (result && result.error) {
          this.$errorMessage({ message: result.error });
        } else {
          this.openConfirmView();
          this.confirmationTextArray = this.indentElapsedTimeArray(result.data);
        }
      } catch (err) {
        this.$errorMessage({ message: this.$t('INDENT_DATE_SAVE_FAILED') });
      } finally {
        this.isLoadingConfirmView = false;
      }
    },
    async confirmDate() {
      try {
        this.isSaveLoading = true;
        const data = [
          {
            unique_id: this.event.unique_id,
          },
        ];
        data[0][this.timelineConstant.api_key_date] = moment(
          this.eventTime,
        ).toISOString();
        const result = await this.$store.dispatch('order/confirmDate', {
          orderId: this.orderId,
          url: this.timelineConstant.confirm_api,
          data,
        });
        if (!result) {
          this.$errorMessage({ message: this.$t('INDENT_DATE_SAVE_FAILED') });
        } else if (result && result.error) {
          this.$errorMessage({ message: result.error });
        } else {
          this.$successMessage({ message: this.$t('INDENT_DATE_SAVE_SUCCESSFUL') });
          this.$store.commit('order/SET_ORDER', result.data);
          this.closeConfirmView();
        }
      } catch (err) {
        this.$errorMessage({ message: this.$t('INDENT_DATE_SAVE_FAILED') });
      } finally {
        this.isSaveLoading = false;
      }
    },
    /**
     * end of api calls
     */
    /**
     * start of editable view changes
     */
    openEditableView() {
      this.init();
      this.isEditableView = true;
    },
    closeEditableView() {
      this.eventTime = this.event.time;
      this.isEditableView = false;
    },
    openConfirmView() {
      this.isEditableView = false;
      this.isConfirmView = true;
    },
    closeConfirmView() {
      this.eventTime = this.event.time;
      this.isConfirmView = false;
      this.init();
    },
    init() {
      this.confirmationTextArray = {};
    },
    /**
     * end of editable view changes
     */

    /**
     * start of date formatting methods
     */
    formatDate(date) {
      return date ? this.$moment(date).format('DD MMM YYYY') : null;
    },
    formatTime(date) {
      return date ? this.$moment(date).format(this.timeFormat) : null;
    },
    formatElapsedTime(elapsedDate, comparisonDate = null) {
      if (isNumber(elapsedDate) && !isDate(elapsedDate)) {
        const prefix = elapsedDate < 0 ? '-' : '+';
        const diff = elapsedDate;
        return { diff, value: `${prefix} ${elapsedDate} day(s)` };
      }
      const date1 = comparisonDate || new Date();
      const date2 = elapsedDate;
      if (date1 && date2) {
        const diff = moment(date1).diff(moment(date2), 'minutes');
        const prefix = diff < 0 ? '-' : '+';
        const absDiff = Math.abs(diff);
        const days = Math.floor(absDiff / (24 * 60));
        const hours = Math.ceil((absDiff % (24 * 60)) / 60);
        return { diff, value: `${prefix} ${days} day(s) ${hours} hour(s)` };
      }
      return {};
    },
    changeTime(value) {
      this.eventTime = value;
    },
    /**
     * end of date formatting methods
     */
  },
};
</script>

<style lang="scss" scoped>
.order-timeline {
  font-size: 14px;
}
.timelinecontentdiv {
  margin: 0 0 10px 0;
  line-height: 22px;
  b {
    color: #646464;
  }
}
.timelineindent {
  color: #646464;
}
</style>
